import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AvailableCardAction,
  Card,
  CardNumberChallengeItem,
  CardStatusEnum,
  CardTypeEnum,
  ReplaceCardChatHoursSuccess,
  Typenames,
} from '@varo/graphql-schema-types';
import { openURL } from 'expo-linking';
import {
  NativeStackHeaderProps,
  router,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import SamsungViewWalletButton from 'features/cardManagement/components/WalletButton/SamsungViewWalletButton';
import WalletButton, {
  isSamsungDevice,
} from 'features/cardManagement/components/WalletButton/WalletButton';
import { CardExpirationStatusEnum } from 'features/cardManagement/helpers/CardExpirationStatusEnum';
import { useCardManagerCardLockExperience } from 'features/cardManagement/hooks/useCardLockExperience/useCardLockExperience';
import useManageCard from 'features/cardManagement/hooks/useManageCard';
import { useCardCredentialChallengeContext } from 'features/cardManagement/providers/CardCredentialChallengeContext';
import { CardAlert } from 'features/cardManagement/screens/ManageCard/components/CardAlert/CardAlert';
import { cardChallengesAtom } from 'features/cardManagement/state/cardChallengeAtom';
import { useSetAtom } from 'jotai';
import {
  default as React,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemTrailingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemTrailingAccessory';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import { isIOS } from 'shared/constants/platform';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

import CardComponent from './components/CardComponent/CardComponent';

const navigateToHelpChat = () => router.push('/myvaro/help/chat');

const goBack = () => {
  if (router.canGoBack()) {
    router.back();
  } else {
    router.replace('/');
  }
};

const onPressSetPinClick = ({
  cardDetails,
  cardId,
  router,
}: {
  cardDetails: Card;
  cardId: string;
  router: typeof import('expo-router').router;
}) => {
  const pathname = cardDetails?.actions?.includes(AvailableCardAction.ChangePin)
    ? `/myvaro/card/change-pin/[cardId]`
    : `/myvaro/card/new-pin/[cardId]`;

  router.push({
    pathname,
    params: {
      cardId,
      cardType: cardDetails?.type,
    },
  });
};

enum BottomSheetContentType {
  ADD_TO_WALLET_LOCKED_CARD = 'ADD_TO_WALLET_LOCKED_CARD',
  REPLACE_LOST_STOLEN_CHAT = 'REPLACE_LOST_STOLEN_CHAT',
}

const ManageCard = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('cardManagement', {
    keyPrefix: 'manageCard',
  });
  const { cardId, challengeJustCompleted, completedChallengeId } =
    useLocalSearchParams<{
      cardId: string;
      challengeJustCompleted?: string;
      completedChallengeId?: string;
    }>();
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const { bottom } = useSafeAreaInsets();
  const [triggerWalletFlow, setTriggerWalletFlow] = useState<boolean>(false);
  const challengeReturnHandledRef = useRef(false);

  const [bottomSheetContentType, setBottomSheetContentType] =
    useState<BottomSheetContentType | null>(null);

  const { toggle: toggleLanguage, bottomSheet: bottomSheetLanguage } =
    useCardManagerCardLockExperience();

  const { loadChallenges } = useCardCredentialChallengeContext();
  const setCardChallenges = useSetAtom(cardChallengesAtom);

  const handleChallenge = useCallback(
    (challenges: CardNumberChallengeItem[]) => {
      loadChallenges(challenges);
      setCardChallenges(challenges);
      router.push({
        pathname: '/myvaro/card/credential-challenge/[cardId]/confirm-identity',
        params: { cardId },
      });
    },
    [loadChallenges, setCardChallenges, cardId, router],
  );

  const handleDeclined = useCallback(() => {
    router.push({
      pathname: '/myvaro/card/credential-challenge/[cardId]/declined',
      params: { cardId },
    });
  }, [cardId, router]);

  const {
    fetchingLockCardMutation,
    handleLockCard,
    errorData,
    cardDetails,
    userDetails,
    cardExpiryStatus,
    fetchingCardAndUser,
    fetchCardAndUserError,
    cardCVVResponse,
    fetchingCVV,
    fetchCardCVVError,
    getCvvDetails,
    replaceCardData,
    fetchingReplaceCardChatHours,
    fetchReplaceCardChatHoursError,
    getReplaceCardChatHours,
  } = useManageCard({
    cardId,
    onChallenge: handleChallenge,
    onDeclined: handleDeclined,
  });
  const replaceCardsData = replaceCardData as ReplaceCardChatHoursSuccess;

  useEffect(() => {
    if (
      challengeJustCompleted !== 'true' ||
      challengeReturnHandledRef.current
    ) {
      return;
    }
    challengeReturnHandledRef.current = true;
    void getCvvDetails(completedChallengeId);
  }, [challengeJustCompleted, completedChallengeId, getCvvDetails]);

  const { isLocked: isCardLocked } = cardDetails || {};

  const showErrorScreen =
    (cardCVVResponse.error?.__typename === Typenames.CardNumberError ||
      cardCVVResponse.error?.__typename === Typenames.CardNumberNotEligible ||
      fetchCardCVVError ||
      errorData ||
      fetchReplaceCardChatHoursError ||
      fetchCardAndUserError) &&
    !fetchingLockCardMutation;

  useViewDidFocusScreenAnalyticsEvent(
    cardDetails?.type
      ? {
          key: 'Card Management',
          customAttributes: {
            card_type: cardDetails?.type?.toLowerCase() ?? '',
          },
        }
      : null,
  );

  const cardActions = {
    canLockUnlockCard:
      cardDetails?.actions?.includes(AvailableCardAction.Unlock) ||
      cardDetails?.actions?.includes(AvailableCardAction.Lock),
    canChangeSetPin:
      cardDetails?.actions?.includes(AvailableCardAction.ChangePin) ||
      cardDetails?.actions?.includes(AvailableCardAction.SetPin),
  };

  const handleUnlockCardBottomSheet = () => {
    void handleLockCard(false);
    bottomSheetRef.current?.close();
  };

  const getHeaderTitle = () => {
    if (cardDetails?.type === CardTypeEnum.ChargeCard) {
      return translatedText('cardTitle.varoBelieve');
    }
    if (cardDetails?.type === CardTypeEnum.DebitCard) {
      return translatedText('cardTitle.varoDebit');
    }
    return '';
  };

  const gotoReplaceCardFlow = () => {
    router.push({
      pathname: '/myvaro/card/replace-lost-stolen/replace-card/[cardId]',
      params: {
        cardId: cardId,
        cardType: cardDetails?.type,
      },
    });
  };

  const cardAlertClickAction = () => {
    if (isCardExpired) {
      navigateToHelpChat();
    } else {
      onPressSetPinClick({
        cardDetails: cardDetails as Card,
        cardId,
        router,
      });
    }
  };

  const onPressReportLostOrStolenCard = () => {
    if (cardDetails?.isReplaceable) {
      gotoReplaceCardFlow();
    } else {
      setBottomSheetContentType(
        BottomSheetContentType.REPLACE_LOST_STOLEN_CHAT,
      );
      getReplaceCardChatHours();
      bottomSheetRef?.current?.present();
    }
  };

  const onPressChatNow = () => {
    bottomSheetRef.current?.close();
    navigateToHelpChat();
  };

  const listData = [
    {
      condition: cardActions.canLockUnlockCard,
      analyticsId: isCardLocked ? 'cardLockClicked' : 'cardUnlockClicked',
      primaryCopy: isCardLocked
        ? toggleLanguage.afterAction
        : toggleLanguage.beforeAction,
      trailingAccessory: {
        id: cardId,
        name: VXLComponentId.SWITCH,
        props: {
          isOn: isCardLocked || false,
          analyticsId: isCardLocked ? 'cardUnlockClicked' : 'cardLockClicked',
          onValueChange: (locked) => {
            void handleLockCard(locked as boolean);
          },
          isLoading: fetchingLockCardMutation,
          customAttributes: {
            card_type: cardDetails?.type?.toLowerCase(),
          },
        },
      },
      onPress: null,
      extraComponent: (
        <BodyXSText style={styles.lockAccessoryContent} numberOfLines={0}>
          {toggleLanguage.info}
        </BodyXSText>
      ),
      showChevron: false,
    },
    {
      condition: cardActions.canChangeSetPin && !isCardLocked,
      testID: 'manage-card-pin-button',
      primaryCopyTestID: 'manage-card-pin-button-primary-label',
      analyticsId: 'setPinClicked',
      primaryCopy: cardDetails?.actions?.includes(AvailableCardAction.ChangePin)
        ? translatedText('pin.changePin')
        : translatedText('pin.createPin'),
      onPress: () =>
        onPressSetPinClick({
          cardDetails: cardDetails as Card,
          cardId,
          router,
        }),
      extraComponent: null,
      showChevron: true,
    },
    {
      condition: true,
      testID: 'manage-card-report-lost-stolen-button',
      analyticsId: 'orderPhysicalCardFlowInitiated',
      customAttributes: {
        cta: 'Report lost or stolen card',
      },
      primaryCopy: translatedText('reportLostStolen'),
      trailingAccessory: null,
      onPress: onPressReportLostOrStolenCard,
      extraComponent: null,
      showChevron: true,
    },
  ];

  const shouldShowSetPinAction =
    isNotNil(cardDetails) &&
    isNotNil(isCardLocked) &&
    !cardDetails.isPinSet &&
    !isCardLocked &&
    cardDetails.status !== CardStatusEnum.New &&
    cardDetails.status !== CardStatusEnum.Suspended;

  const isCardExpired = cardExpiryStatus === CardExpirationStatusEnum.Expired;

  const canShowCardActions =
    cardDetails &&
    cardDetails.status !== CardStatusEnum.New &&
    cardDetails.status !== CardStatusEnum.Suspended &&
    !isCardExpired;

  let sheetContent: React.ReactElement | null = null;
  if (
    bottomSheetContentType === BottomSheetContentType.ADD_TO_WALLET_LOCKED_CARD
  ) {
    sheetContent = (
      <View style={styles.bottomSheet}>
        <TitleSmText numberOfLines={0}>
          {bottomSheetLanguage.wallet.header}
        </TitleSmText>
        <BodyBaseText style={styles.bottomSheetSubTitle} numberOfLines={0}>
          {bottomSheetLanguage.wallet.body}
        </BodyBaseText>
        <View style={styles.contentView}>
          <Button
            label={bottomSheetLanguage.buttons.enableCard}
            type="PRIMARY"
            onPress={handleUnlockCardBottomSheet}
            analyticsId="cardUnlockClicked"
            customAttributes={{
              card_type: cardDetails?.type?.toLowerCase(),
            }}
          />
        </View>
      </View>
    );
  } else if (
    bottomSheetContentType === BottomSheetContentType.REPLACE_LOST_STOLEN_CHAT
  ) {
    sheetContent = (
      <View>
        {fetchingReplaceCardChatHours ? (
          <View style={styles.bottomSheetContainer}>
            <LoadingSpinner />
          </View>
        ) : (
          <View style={styles.bottomSheet}>
            <TitleSmText numberOfLines={0}>
              {replaceCardsData?.header ?? ''}
            </TitleSmText>
            <BodyBaseText style={styles.bottomSheetSubTitle} numberOfLines={0}>
              {replaceCardsData?.subTitle ?? ''}
              <HyperlinkBaseText
                onPress={async () => {
                  await openURL(`${replaceCardsData?.supportTicketLink?.url}`);
                }}
              >
                {replaceCardsData?.supportTicketLink?.title ?? ''}
              </HyperlinkBaseText>
            </BodyBaseText>
            {replaceCardsData?.footer ? (
              <BodyBaseText
                style={styles.bottomSheetSubTitle}
                numberOfLines={0}
              >
                {replaceCardsData.footer}
              </BodyBaseText>
            ) : null}
            <View style={styles.contentView}>
              <Button
                label={translatedText('bottomSheet.buttons.chatNow')}
                type="PRIMARY"
                onPress={onPressChatNow}
                customAttributes={{
                  card_type: cardDetails?.type?.toLowerCase(),
                }}
                isDisabled={!replaceCardsData?.isChatEnabled}
              />
            </View>
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          title: getHeaderTitle(),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          headerShown: true,
          headerBackVisible: true,
        }}
      />

      <ScrollView>
        <CardAlert
          shouldShowSetPinAction={shouldShowSetPinAction}
          translatedText={translatedText}
          cardType={cardDetails?.type as CardTypeEnum}
          cardStatus={cardDetails?.status as CardStatusEnum}
          cardExpiryStatus={cardExpiryStatus as CardExpirationStatusEnum}
          onClick={cardAlertClickAction}
        />

        {cardDetails ? (
          <CardComponent
            card={cardDetails}
            cvvData={cardCVVResponse.data}
            isExpired={isCardExpired}
            firstName={userDetails?.customerInfo?.name?.first ?? ''}
            lastName={userDetails?.customerInfo?.name?.last ?? ''}
            isLoading={fetchingCVV}
            getCvvDetails={getCvvDetails}
          />
        ) : null}
        {canShowCardActions ? (
          <>
            {listData
              .filter((item) => item.condition)
              .map((item, index) => (
                <React.Fragment key={item.analyticsId}>
                  <ListItem
                    testID={item.testID}
                    primaryCopyTestID={item.primaryCopyTestID}
                    analyticsId={item.analyticsId}
                    customAttributes={{
                      ...item.customAttributes,
                      card_type: cardDetails?.type?.toLowerCase(),
                    }}
                    primaryCopy={item.primaryCopy}
                    trailingAccessory={
                      item.trailingAccessory as ListItemTrailingAccessoryProps
                    }
                    onPress={item.onPress}
                    showsChevron={item.showChevron}
                  />
                  {item.extraComponent}
                  {index < listData.length - 1 && !item.extraComponent ? (
                    <View style={styles.hairLine}>
                      <HairLineDivider />
                    </View>
                  ) : null}
                </React.Fragment>
              ))}

            <WalletButton
              style={styles.walletProvisioningButton}
              cardId={cardId}
              maskedCardNumber={cardDetails?.maskedCardNumber ?? ''}
              cardholderName={userDetails?.customerInfo?.name?.full ?? ''}
              isDebit={cardDetails?.type === CardTypeEnum.DebitCard}
              manuallyTriggerFlow={triggerWalletFlow}
              enableProvisioningOnTap={false}
              onButtonPress={() => {
                if (isCardLocked) {
                  setBottomSheetContentType(
                    BottomSheetContentType.ADD_TO_WALLET_LOCKED_CARD,
                  );
                  bottomSheetRef?.current?.present();
                } else {
                  setTriggerWalletFlow(true);
                }
              }}
              onFlowComplete={() => {
                setTriggerWalletFlow(false);
              }}
              sentryPageName="Manage Card"
            />
            {isSamsungDevice() ? (
              <SamsungViewWalletButton
                style={styles.walletProvisioningButton}
              />
            ) : null}
          </>
        ) : null}
        <View style={styles.contentView}>
          <BodyXSText style={[styles.content, styles.label]} numberOfLines={0}>
            {translatedText(
              cardDetails?.type === CardTypeEnum.DebitCard
                ? 'debitInfo'
                : 'believeInfo',
            )}
          </BodyXSText>
        </View>
      </ScrollView>

      <BottomSheet ref={bottomSheetRef}>{sheetContent}</BottomSheet>

      <ScreenTransitionOverlay
        isLoading={fetchingCardAndUser}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          showErrorScreen ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={
                errorData?.title ?? translatedText('error.title')
              }
              messageBody={
                errorData?.message ?? translatedText('error.message')
              }
              primaryButton={{
                title: translatedText('error.button'),
                onPress: goBack,
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheet: {
    paddingHorizontal: theme.spacing.m,
  },
  bottomSheetContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.xs,
    padding: theme.spacing.m,
  },
  bottomSheetSubTitle: {
    paddingVertical: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  content: { padding: theme.spacing.m },
  contentView: { paddingVertical: theme.spacing.m },
  hairLine: {
    marginHorizontal: theme.spacing.m,
  },
  label: { color: theme.color.text.secondary },
  lockAccessoryContent: { paddingHorizontal: theme.spacing.m },
  walletProvisioningButton: {
    height: 40,
    margin: isIOS ? theme.spacing.m : theme.spacing.s,
  },
});

export default ManageCard;
